// src/pages/Request.js
import { useState, useEffect, useRef } from "react";
import { submitPickupRequest } from "../services/api";

const BASE = "https://api.pulselogistics.com.ng/api";

const getQuote = async (params) => {
  if (!params.senderAddress || !params.recipientAddress) return null;
  const query = new URLSearchParams({
    senderAddress:   params.senderAddress,
    recipientAddress: params.recipientAddress,
    weightKg:        params.weightKg     || 0,
    length:          params.length       || 0,
    width:           params.width        || 0,
    height:          params.height       || 0,
    items:           params.items        || 1,
    contentType:     params.contentType  || "standard",
    urgency:         params.urgency      || "standard",
    insurance:       params.insurance    || "false",
  });
  const res = await fetch(`${BASE}/pricing/quote?${query}`, { credentials: "include" });
  return res.json();
};

export default function Request() {
  const [senderName,       setSenderName]       = useState("");
  const [senderPhone,      setSenderPhone]       = useState("");
  const [senderAddress,    setSenderAddress]     = useState("");
  const [recipientName,    setRecipientName]     = useState("");
  const [recipientPhone,   setRecipientPhone]    = useState("");
  const [recipientAddress, setRecipientAddress]  = useState("");
  const [weightKg,         setWeightKg]          = useState("");
  const [length,           setLength]            = useState("");
  const [width,            setWidth]             = useState("");
  const [height,           setHeight]            = useState("");
  const [items,            setItems]             = useState(1);
  const [contentType,      setContentType]       = useState("standard");
  const [urgency,          setUrgency]           = useState("standard");
  const [insurance,        setInsurance]         = useState(false);
  const [notes,            setNotes]             = useState("");
  const [submitted,        setSubmitted]         = useState(false);
  const [loading,          setLoading]           = useState(false);
  const [error,            setError]             = useState("");
  const [quote,            setQuote]             = useState(null);
  const [quoteLoading,     setQuoteLoading]      = useState(false);
  const debounceRef = useRef(null);

  const waNumber = "2348023753147";

  useEffect(() => {
    if (!senderAddress || !recipientAddress) { setQuote(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setQuoteLoading(true);
      const data = await getQuote({
        senderAddress, recipientAddress,
        weightKg, length, width, height,
        items, contentType, urgency,
        insurance: insurance ? "true" : "false",
      });
      setQuoteLoading(false);
      if (data?.success) setQuote(data.quote);
      else setQuote(null);
    }, 800);
  }, [senderAddress, recipientAddress, weightKg, length, width, height, items, contentType, urgency, insurance]);

  const waMessage = encodeURIComponent(
    `Hello Pulse Logistics! Pickup request.\n\nSender: ${senderName} - ${senderPhone}\nFrom: ${senderAddress}\nRecipient: ${recipientName} - ${recipientPhone}\nTo: ${recipientAddress}\nWeight: ${weightKg}kg | Dims: ${length}×${width}×${height}cm | Items: ${items}\nType: ${contentType} | Urgency: ${urgency}\nQuote: ${quote?.formatted || "TBD"}\nNotes: ${notes || "None"}`
  );

  const handleSubmit = async () => {
    if (!senderName || !senderPhone || !senderAddress || !recipientName || !recipientAddress) {
      return setError("Please fill in all required fields marked with *");
    }
    setLoading(true); setError("");
    const data = await submitPickupRequest({
      senderName, senderPhone, senderAddress,
      recipientName, recipientPhone, recipientAddress,
      weightKg, shipmentType: contentType, notes,
      quotedPrice: quote?.totalPrice || null,
    });
    setLoading(false);
    if (data?.success) setSubmitted(true);
    else setError(data?.message || "Something went wrong. Please try again.");
  };

  const resetForm = () => {
    setSenderName(""); setSenderPhone(""); setSenderAddress("");
    setRecipientName(""); setRecipientPhone(""); setRecipientAddress("");
    setWeightKg(""); setLength(""); setWidth(""); setHeight("");
    setItems(1); setContentType("standard"); setUrgency("standard");
    setInsurance(false); setNotes(""); setSubmitted(false); setError(""); setQuote(null);
  };

  if (submitted) return (
    <div style={{ minHeight: "80vh", background: "#F7FAFB", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 28px" }}>✅</div>
        <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 28, color: "#0B1E3D", marginBottom: 10 }}>Request received!</h2>
        {quote && (
          <div style={{ background: "#EEF4F7", borderRadius: 10, padding: "14px 20px", marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Total Quote</div>
            <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 32, color: "#00A896" }}>{quote.formatted}</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{quote.routeLabel}</div>
          </div>
        )}
        <p style={{ color: "#6B7280", marginBottom: 32, lineHeight: 1.7 }}>Our team will confirm within 30 minutes. Confirm on WhatsApp for the fastest response.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <a href={`https://wa.me/${waNumber}?text=${waMessage}`} target="_blank" rel="noopener noreferrer" className="btn btn-wa">💬 Confirm on WhatsApp</a>
          <button className="btn btn-ghost" onClick={resetForm}>New Request</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>
      <div className="dark-header">
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <span className="tag tag-dark" style={{ marginBottom: 14, display: "inline-block" }}>PICKUP REQUEST</span>
          <h1>Schedule a pickup</h1>
          <p>Fill in the details below — get an instant price quote.</p>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>

        {/* Sender */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #E2EBF0" }}>📍 Sender Details</div>
          <div className="form-grid-2">
            <div className="form-group"><label>Full Name *</label><input type="text" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="e.g. Comfort Okafor" /></div>
            <div className="form-group"><label>Phone Number *</label><input type="tel" value={senderPhone} onChange={e => setSenderPhone(e.target.value)} placeholder="e.g. 08012345678" /></div>
          </div>
          <div className="form-group">
            <label>Pickup Address * <span style={{ color: "#9CA3AF", fontSize: 11 }}>(include city e.g. "Trans Amadi, Port Harcourt")</span></label>
            <input type="text" value={senderAddress} onChange={e => setSenderAddress(e.target.value)} placeholder="e.g. 12 Trans Amadi Road, Port Harcourt" />
          </div>
        </div>

        {/* Recipient */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #E2EBF0" }}>📦 Recipient Details</div>
          <div className="form-grid-2">
            <div className="form-group"><label>Full Name *</label><input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="e.g. Emeka Nwosu" /></div>
            <div className="form-group"><label>Phone Number</label><input type="tel" value={recipientPhone} onChange={e => setRecipientPhone(e.target.value)} placeholder="e.g. 07098765432" /></div>
          </div>
          <div className="form-group">
            <label>Delivery Address * <span style={{ color: "#9CA3AF", fontSize: 11 }}>(include city e.g. "Lekki, Lagos")</span></label>
            <input type="text" value={recipientAddress} onChange={e => setRecipientAddress(e.target.value)} placeholder="e.g. 5 Admiralty Way, Lekki, Lagos" />
          </div>
        </div>

        {/* Cargo Details */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #E2EBF0" }}>📋 Cargo Details</div>

          <div className="form-grid-2">
            <div className="form-group"><label>Actual Weight (kg)</label><input type="number" value={weightKg} onChange={e => setWeightKg(e.target.value)} placeholder="e.g. 12" min="0" step="0.1" /></div>
            <div className="form-group"><label>Number of Items</label><input type="number" value={items} onChange={e => setItems(e.target.value)} placeholder="e.g. 3" min="1" step="1" /></div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
              Dimensions (cm) <span style={{ color: "#9CA3AF", fontSize: 11 }}>— for volumetric weight calculation</span>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 11 }}>Length</label><input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="cm" min="0" /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 11 }}>Width</label><input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="cm" min="0" /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: 11 }}>Height</label><input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="cm" min="0" /></div>
            </div>
            {length && width && height && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#00A896", fontWeight: 600 }}>
                Volumetric weight: {Math.round((parseFloat(length) * parseFloat(width) * parseFloat(height)) / 5000 * 10) / 10} kg
                {weightKg && <span style={{ color: "#6B7280", fontWeight: 400 }}> · Chargeable: <strong style={{ color: "#0B1E3D" }}>{Math.max(parseFloat(weightKg) || 0, Math.round((parseFloat(length) * parseFloat(width) * parseFloat(height)) / 5000 * 10) / 10)} kg</strong></span>}
              </div>
            )}
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label>Content Type</label>
              <select value={contentType} onChange={e => setContentType(e.target.value)}>
                <option value="standard">📦 Standard</option>
                <option value="fragile">🔮 Fragile (+₦2,000)</option>
                <option value="perishable">🌡️ Perishable (+₦1,500)</option>
                <option value="hazardous">⚠️ Hazardous (+₦5,000)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Urgency</label>
              <select value={urgency} onChange={e => setUrgency(e.target.value)}>
                <option value="standard">🕐 Standard (24–72 hrs)</option>
                <option value="express">⚡ Express (+20%)</option>
                <option value="same_day">🚀 Same-day (+30%)</option>
              </select>
            </div>
          </div>

          {/* Insurance checkbox */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#F0FDF4", borderRadius: 8, border: "1px solid #BBF7D0", marginBottom: 16 }}>
            <input type="checkbox" id="insurance" checked={insurance} onChange={e => setInsurance(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#00A896", cursor: "pointer" }} />
            <label htmlFor="insurance" style={{ fontSize: 13, color: "#374151", cursor: "pointer", margin: 0 }}>
              🛡️ Add Insurance <span style={{ color: "#6B7280" }}>(1.5% of total — covers loss or damage)</span>
            </label>
          </div>

          <div className="form-group"><label>Additional Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Special handling instructions, preferred pickup window, etc." /></div>
        </div>

        {/* Live Quote */}
        {(senderAddress && recipientAddress) && (
          <div style={{ marginBottom: 20, borderRadius: 12, overflow: "hidden", border: quote ? "2px solid #00C6D7" : "1px solid #E2EBF0", background: "#fff" }}>
            <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff" }}>💰 Instant Price Quote</div>
              {quoteLoading && <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)" }}>Calculating...</div>}
            </div>
            <div style={{ padding: "20px 24px" }}>
              {quoteLoading && <div style={{ textAlign: "center", padding: "12px 0", color: "#6B7280", fontSize: 13 }}>⏳ Getting your quote...</div>}
              {!quoteLoading && quote && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Route</div>
                      <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 14 }}>{quote.routeLabel}</div>
                      {quote.volumetricKg > quote.actualKg && quote.volumetricKg > 0 && (
                        <div style={{ fontSize: 11, color: "#D97706", marginTop: 2 }}>⚠ Volumetric weight applied ({quote.volumetricKg}kg)</div>
                      )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>Total</div>
                      <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 32, color: "#00A896" }}>{quote.formatted}</div>
                    </div>
                  </div>

                  {/* Breakdown */}
                  <div style={{ background: "#F7FAFB", borderRadius: 8, padding: "12px 16px" }}>
                    {quote.breakdown.map((b, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: i < quote.breakdown.length - 1 ? "1px solid #E2EBF0" : "none" }}>
                        <span style={{ color: "#6B7280" }}>{b.label}</span>
                        <span style={{ fontWeight: 600, color: b.amount === 0 ? "#9CA3AF" : "#0B1E3D" }}>{b.amount === 0 ? "—" : `₦${b.amount.toLocaleString()}`}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, padding: "8px 0 0", marginTop: 4, borderTop: "2px solid #E2EBF0", color: "#0B1E3D" }}>
                      <span>Total</span>
                      <span style={{ color: "#00A896" }}>{quote.formatted}</span>
                    </div>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: "#9CA3AF", textAlign: "center" }}>* Estimate only. Final price confirmed by our team.</div>
                </div>
              )}
              {!quoteLoading && !quote && (
                <div style={{ textAlign: "center", padding: "8px 0", color: "#6B7280", fontSize: 13 }}>Enter both addresses above to see your price quote</div>
              )}
            </div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={handleSubmit} disabled={loading}>
            {loading ? "Submitting…" : "📦 Submit Request"}
          </button>
          <a href={`https://wa.me/${waNumber}?text=${waMessage}`} target="_blank" rel="noopener noreferrer" className="btn btn-wa" style={{ flex: 1, justifyContent: "center" }}>
            💬 Send via WhatsApp
          </a>
        </div>
        <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: 12, marginTop: 16 }}>* Required fields</p>
      </div>
    </div>
  );
}